import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import StageBadge from "@/components/StageBadge";
import ScanMethodBadge, { summarizeMethods } from "@/components/order/ScanMethodBadge";
import { Clock, User } from "@phosphor-icons/react";
import { ROLE_LABELS } from "@/lib/stages";

/**
 * Per-item full audit trail. Fetches `/api/items/{id}/history` from the
 * deployed backend on open. Shows every scan event (who/what/when) in
 * reverse-chronological order.
 */
export default function ItemHistoryDialog({ item, open, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !item?.id) return;
    let alive = true;
    setLoading(true);
    setError("");
    setHistory(null);
    (async () => {
      try {
        const { data } = await api.get(`/items/${item.id}/history`);
        if (alive) setHistory(data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || "Failed to load history");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, item?.id]);

  const scans = (history?.scans || []).slice().reverse();
  const methodCounts = useMemo(() => summarizeMethods(scans), [scans]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="item-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            History
            {item && <span className="font-mono text-base text-zinc-500 ml-2">· {item.sku}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-zinc-500">Loading history…</div>}
        {error && (
          <div className="px-4 py-3 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {!loading && !error && scans.length === 0 && (
          <div className="py-8 text-center text-zinc-500" data-testid="item-history-empty">
            No scans recorded yet for this item.
          </div>
        )}

        {!loading && !error && scans.length > 0 && (
          <>
            <MethodSummary counts={methodCounts} total={scans.length} />
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="item-history-list">
              {scans.map((s, i) => (
                <HistoryEntry key={s.id || i} entry={s} />
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Compact summary strip like "4 scans · 2 manual · 1 legacy". Only renders
 * segments with non-zero counts so a single-method history stays uncluttered.
 */
function MethodSummary({ counts, total }) {
  const parts = [];
  if (counts.scan)   parts.push({ k: "scan",   label: `${counts.scan} scan${counts.scan === 1 ? "" : "s"}`,       cls: "text-blue-800 dark:text-blue-300" });
  if (counts.manual) parts.push({ k: "manual", label: `${counts.manual} manual`,                                   cls: "text-amber-800 dark:text-amber-300" });
  if (counts.legacy) parts.push({ k: "legacy", label: `${counts.legacy} legacy`,                                   cls: "text-zinc-600 dark:text-zinc-400" });
  if (parts.length <= 1) return null;
  return (
    <div
      className="text-[11px] uppercase tracking-[0.15em] font-bold flex flex-wrap items-center gap-x-2 gap-y-1 pb-2 border-b border-zinc-200 dark:border-zinc-800 mb-1"
      data-testid="item-history-method-summary"
    >
      <span className="text-zinc-500">{total} event{total === 1 ? "" : "s"}</span>
      {parts.map((p, i) => (
        <React.Fragment key={p.k}>
          <span className="text-zinc-400">·</span>
          <span className={p.cls} data-testid={`method-summary-${p.k}`}>{p.label}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function HistoryEntry({ entry }) {
  const step = entry.step || entry.stage || entry.to_step;
  const when = entry.at || entry.timestamp || entry.created_at;
  const userName = entry.user_name || entry.by_user_name || entry.user || "unknown";
  const userRole = entry.user_role || entry.by_user_role;
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border border-zinc-200 bg-white dark:bg-zinc-800 dark:border-zinc-700">
      <div className="shrink-0 mt-0.5">
        {step ? <StageBadge step={step} size="sm" /> : <span className="text-xs text-zinc-400">—</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold flex-wrap">
          <User size={14} weight="bold" className="text-zinc-500" />
          {userName}
          {userRole && (
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold ml-1">
              · {ROLE_LABELS[userRole] || userRole}
            </span>
          )}
          <ScanMethodBadge method={entry.method} />
        </div>
        {entry.reason && (
          <div className="text-xs text-red-700 italic mt-0.5">"{entry.reason}"</div>
        )}
        {entry.notes && (
          <div className="text-xs text-zinc-600 mt-0.5">{entry.notes}</div>
        )}
      </div>
      {when && (
        <div className="text-xs text-zinc-400 font-mono whitespace-nowrap flex items-center gap-1">
          <Clock size={12} weight="bold" />
          {new Date(when).toLocaleString()}
        </div>
      )}
    </div>
  );
}
